import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ArrowsPointingInIcon,
  MagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import { DataTable } from '@oysterjs/ui/data-table';
import { PageTitle } from '@oysterjs/ui/page';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import { Spinner } from '@oysterjs/ui/spinner';
import { TableHeader } from '@oysterjs/ui/table';

import {
  ClientFieldsFragment,
  ClientListFieldsFragment,
  useClientListQuery,
  useClientMergeCandidateCountQuery
} from '~/api/graphql';

import { ConnectionLoadMoreFooter } from '~/components/connection-load-more-footer';
import { ClientFormDialog } from '~/components/client-form';
import {
  ClientMergeDialog,
  ClientMergeGroup,
  MAX_CLIENTS_PER_MERGE
} from '~/components/clients/client-merge-dialog';
import {
  CLIENT_COLUMNS,
  ClientSortKey,
  clientSortToClientSortInput
} from '~/components/clients/clients-columns';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import {
  activeFiltersToClientFilter,
  createClientFilterDefinitions,
  type ClientFilter
} from '~/api/data/clients/clients-filters';

const CLIENTS_PAGE_SIZE = 25;

type SelectableClient = Pick<ClientFieldsFragment, 'id' | 'name'>;

const EmptyState = (props: {
  title: string;
  description: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) => {
  const Icon = props.icon ?? UserGroupIcon;
  return (
    <div className="px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
      <div className="text-center py-12">
        <Icon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {props.title}
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>
      </div>
    </div>
  );
};

export const ClientsPageLoader = () => (
  <div className="px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
    <div className="text-center py-12">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Loading clients...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);

/**
 * Absent for the agencies with nothing to review, which is most of them, so the
 * Clients page does not carry a permanent link to an empty page.
 */
const DuplicatesLink = () => {
  const { data } = useClientMergeCandidateCountQuery();
  const count = data?.clientMergeCandidates.totalCount ?? 0;
  if (count === 0) {
    return null;
  }
  return (
    <Button outline href="/app/clients/duplicates">
      {count === 1 ? '1 possible duplicate' : `${count} possible duplicates`}
    </Button>
  );
};

const toggleSelected = <T extends SelectableClient>(selected: T[], client: T): T[] =>
  selected.some((c) => c.id === client.id)
    ? selected.filter((c) => c.id !== client.id)
    : [...selected, client];

/** The header box acts on the rows on screen only. */
const toggleAllShown = <T extends SelectableClient>(selected: T[], shown: T[]): T[] => {
  const selectedIds = new Set(selected.map((c) => c.id));
  if (shown.length > 0 && shown.every((c) => selectedIds.has(c.id))) {
    const shownIds = new Set(shown.map((c) => c.id));
    return selected.filter((c) => !shownIds.has(c.id));
  }
  return [...selected, ...shown.filter((c) => !selectedIds.has(c.id))];
};

/**
 * The row itself still opens the client, so the box carries noHref. Not the
 * library's selectionColumn: picks here are whole clients that outlive a change
 * of filter, where that one holds ids and its header box replaces them all.
 */
const selectColumn = <T extends SelectableClient>(
  selected: T[],
  shown: T[],
  setSelected: (next: T[]) => void
): ColumnDef<T, ClientSortKey> => {
  const selectedIds = new Set(selected.map((c) => c.id));
  const shownSelected = shown.filter((c) => selectedIds.has(c.id)).length;
  return {
    id: 'select',
    renderHeader: () => (
      <TableHeader className="w-0 py-1.5 pl-3 pr-0">
        <Checkbox
          color="sky"
          aria-label="Select all shown clients"
          checked={shown.length > 0 && shownSelected === shown.length}
          indeterminate={shownSelected > 0 && shownSelected < shown.length}
          onChange={() => setSelected(toggleAllShown(selected, shown))}
        />
      </TableHeader>
    ),
    cellClassName: 'w-0 py-1.5 pl-3 pr-0',
    noHref: true,
    renderCell: (client) => (
      <Checkbox
        color="sky"
        aria-label={`Select ${client.name}`}
        checked={selectedIds.has(client.id)}
        onChange={() => setSelected(toggleSelected(selected, client))}
      />
    )
  };
};

/** Why the merge action is unavailable, or null when it is available. */
export const mergeBlockedReason = (count: number): string | null => {
  if (count < 2) {
    return 'Pick one more to merge';
  }
  if (count > MAX_CLIENTS_PER_MERGE) {
    return `Merge at most ${MAX_CLIENTS_PER_MERGE} at once`;
  }
  return null;
};

export const ClientsPage = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = React.useState(false);
  // Held apart from the rows because filtering empties the rows: the ordinary
  // way to find the next record is to search for it, and the earlier picks have
  // to survive that.
  const [selected, setSelected] = React.useState<ClientListFieldsFragment[]>([]);
  const [mergeGroup, setMergeGroup] = React.useState<ClientMergeGroup | null>(null);

  const filterDefinitions = React.useMemo(() => createClientFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<ClientFilter, ClientSortKey>(
      'clients_default_view',
      filterDefinitions,
      { defaultSortBy: 'name', defaultSortDirection: 'asc' }
    );

  const filter = React.useMemo(() => activeFiltersToClientFilter(activeFilters), [activeFilters]);
  const sort = React.useMemo(
    () => clientSortToClientSortInput(sortBy ?? 'name', sortDirection),
    [sortBy, sortDirection]
  );

  const { data, loading, fetchMore } = useClientListQuery({
    variables: { first: CLIENTS_PAGE_SIZE, filter, sort },
    notifyOnNetworkStatusChange: true
  });

  const connection = data?.clients;
  const clients = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;
  const totalCount = connection?.totalCount ?? 0;

  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage) return;
    fetchMore({ variables: { after: endCursor, first: CLIENTS_PAGE_SIZE, filter, sort } });
  }, [endCursor, hasNextPage, fetchMore, filter, sort]);

  const columns = [selectColumn(selected, clients, setSelected), ...CLIENT_COLUMNS];
  const mergeBlocked = mergeBlockedReason(selected.length);

  const onMerged = () => {
    setMergeGroup(null);
    setSelected([]);
  };

  return (
    <PageTitle title="Clients" description="The businesses and individuals your agency serves">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1 basis-80">
            <SearchFilterInput
              filterDefinitions={filterDefinitions}
              onFiltersChange={setActiveFilters}
              initialFilters={initialFilters}
              placeholder="Filter by line, owner, last activity, or search..."
            />
          </div>
          <div className="flex items-center gap-2">
            <DuplicatesLink />
            <Button color="sky" onClick={() => setCreating(true)}>
              New client
            </Button>
          </div>
        </div>

        <FloatingSelectionBar
          count={selected.length}
          onClear={() => setSelected([])}
          note={mergeBlocked}
          onArchive={() => setMergeGroup({ clientIds: selected.map((client) => client.id) })}
          archiveLabel="Merge clients"
          archiveIcon={<ArrowsPointingInIcon className="size-3.5" />}
          archiveEnabled={mergeBlocked === null}
          isArchiving={false}
        />

        {loading && clients.length === 0 ? (
          <ClientsPageLoader />
        ) : totalCount === 0 ? (
          <EmptyState
            icon={activeFilters.length > 0 ? MagnifyingGlassIcon : UserGroupIcon}
            title={activeFilters.length > 0 ? 'No clients found' : 'No clients yet'}
            description={
              activeFilters.length > 0
                ? 'Try adjusting your search or filters.'
                : 'Clients appear here as your agency works with them.'
            }
          />
        ) : (
          <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
            <DataTable
              data={clients}
              columns={columns}
              keyExtractor={(client) => client.id}
              rowHref={(client) => `/app/clients/${client.id}`}
              tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
              sortKey={sortBy}
              sortDirection={sortDirection}
              onSort={setSort}
              loading={loading}
            />
            <ConnectionLoadMoreFooter
              shown={clients.length}
              totalCount={totalCount}
              noun="clients"
              hasNextPage={hasNextPage}
              onLoadMore={loadMore}
              loading={loading}
            />
          </div>
        )}

        {/* Onward to the new client: the list behind this does not hold it yet. */}
        <ClientFormDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(created) => navigate(`/app/clients/${created.id}`)}
        />

        <ClientMergeDialog
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onMerged={onMerged}
        />
      </div>
    </PageTitle>
  );
};
